import { NavLink } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { api } from '../api.js';
import { timeAgo } from '../util/time.js';

// T100 — Scan health banner. The scanner has always RECORDED its failures
// (DailyScan.fatalError, per-source errors) — this is the first surface
// that shows them. Renders nothing when everything is fine, a red banner
// when the loop itself is broken (fatal error / cron stopped), amber when
// individual sources are failing or silently returning nothing.

// `lastScanAt` — pass the latest scan timestamp from the parent's stats;
// when an in-page manual scan lands a new one, the effect re-runs and the
// banner reflects the fresh state instead of the mount-time snapshot.
export default function ScanHealthBanner({ lastScanAt = null }) {
  const [health, setHealth] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .get('/scans/health')
      .then((h) => alive && setHealth(h))
      // Health is advisory — a failed fetch must never add noise of its own.
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [lastScanAt]);

  if (!health || health.status === 'ok' || dismissed) return null;

  const isError = health.status === 'error';
  const tone = isError
    ? 'border-red-900/60 bg-red-950/30 text-red-200'
    : 'border-amber-800/60 bg-amber-950/20 text-amber-200';

  let headline;
  let detail = null;
  // T286 — a paused queue speaks first, and in its own words.
  //
  // `mergeQueueHealth` has raised the overall status for a paused queue since
  // T200, but this chain had no branch for it: a pause fell through to the final
  // `else` and rendered "Some sources need attention" with an empty detail,
  // while `queue.message` — written precisely so the operator would read "the
  // queue is PAUSED" instead of "scans timed out" — was dropped on the floor.
  //
  // T287 — but ONLY an error, and only a real one. The first version fired on
  // any non-ok queue status, which put two other things in front of the alarms
  // this component exists for:
  //
  //   * a WARNING about past failed jobs outranked "Last scan failed" and "No
  //     scan in Nh". Paired with a failed-set that never emptied, one bad scan
  //     in August would have hidden every fatal error after it.
  //   * 'unknown' — Redis unreachable — carries a message too, and the backend
  //     deliberately does NOT escalate on it. Rendering it here as the headline
  //     overrode that decision from the other side of the wire.
  //
  // T288 — and it stays unrendered, deliberately. 'unknown' means the CHECK
  // could not run, not that the loop failed, and the consequence it would warn
  // about surfaces through a truer signal: if Redis is unreachable then nothing
  // enqueues, no scan completes, and `health.stale` fires "No scan in Nh" —
  // which measures the actual harm instead of the instrument. Adding a second
  // alarm for the instrument is how a person learns to close alarms.
  //
  // A pause still comes first, because while it holds every other verdict is a
  // symptom of it. A queue warning goes last, after the scan verdicts.
  if (health.queue?.status === 'error' && health.queue.message) {
    headline = 'The scan queue is not running';
    detail = health.queue.message;
  } else if (health.lastScan?.fatalError) {
    headline = 'Last scan failed';
    detail = health.lastScan.fatalError;
  } else if (health.stale) {
    headline = `No scan in ${health.staleHours}h`;
    detail = 'The daily scanner appears to have stopped — check the worker container.';
  } else if (health.queue?.status === 'warn' && health.queue.message) {
    // After the scan verdicts: a past failure is worth surfacing, never worth
    // hiding a live one.
    headline = 'Scan queue needs a look';
    detail = health.queue.message;
  } else {
    const failing = health.failingSources.map((f) => f.source);
    const silent = health.silentSources.map((s) => s.source);
    headline = 'Some sources need attention';
    detail = [
      failing.length > 0 && `erroring: ${failing.join(', ')}`,
      silent.length > 0 && `no results for days: ${silent.join(', ')}`,
    ]
      .filter(Boolean)
      .join(' · ');
  }

  return (
    <div role="status" className={`mt-4 flex items-start gap-3 rounded-xl border p-3 text-sm ${tone}`}>
      <span aria-hidden="true" className="mt-0.5">{isError ? '🔴' : '⚠️'}</span>
      <div className="min-w-0 flex-1">
        <p className="font-medium">
          {headline}
          {health.lastScan?.runAt && (
            <span className="ml-2 font-normal opacity-70">last scan {timeAgo(health.lastScan.runAt)}</span>
          )}
        </p>
        {detail && <p className="mt-0.5 break-words text-xs opacity-80">{detail}</p>}
        {/* T419 — it told him something was broken and left him there.
            Source health used to have four surfaces; now this is the only
            one that speaks up on the dashboard, so it owes him the way to
            the one page that can act on it. */}
        <NavLink
          to="/sources"
          className="mt-1 inline-block text-xs underline decoration-dotted opacity-80 transition hover:opacity-100"
        >
          Check the sources →
        </NavLink>
      </div>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss health warning"
        className="rounded-md px-1.5 text-xs opacity-60 hover:opacity-100"
      >
        ✕
      </button>
    </div>
  );
}
